import React, { ComponentProps } from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, TitleText, useResponsive } from '@garnish/core';
import { ContentfulImage } from '@garnish/media';

import type { Asset } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderIntroCard = (props: GroupOrderIntroCardProps) => {
  const { badge, title, text, image } = props;

  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const imageUrl = image?.fields?.file?.url;
  const imageAlt = image?.fields?.title;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      {imageUrl ? (
        <ContentfulImage
          baseUrl={imageUrl}
          options={IMAGE_OPTIONS}
          contentFit="cover"
          alt={imageAlt}
          style={match([styles.imageXS, styles.imageSM])}
        />
      ) : null}

      <View style={styles.contentContainer}>
        {badge ? (
          <View style={styles.badge}>
            <BodyText sizeMatch={['18']}>{badge}</BodyText>
          </View>
        ) : null}

        <TitleText style={styles.text} sizeMatch={['32']}>
          {title}
        </TitleText>

        <BodyText sizeMatch={['14', '16']} style={styles.text}>
          {text}
        </BodyText>
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_OPTIONS: ContentfulImageOptions = { h: 300, fm: 'webp' };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  imageXS: {
    width: '100%',
    height: 190,
  },
  imageSM: {
    width: '100%',
    height: 220,
  },
  contentContainer: {
    minHeight: 190,
    alignItems: 'flex-start',
    gap: theme.spacing['2'],
    padding: theme.spacing['6'],
  },
  badge: {
    backgroundColor: theme.colors.SWEET_CORN,
    borderRadius: 40,
    paddingHorizontal: theme.spacing['2'],
  },
  badgeText: {
    color: theme.colors.KALE,
  },
  text: {
    color: theme.colors.CREAM,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GroupOrderIntroCardProps = {
  badge?: string;
  title: string;
  text: string;
  image?: Asset;
};

type ContentfulImageOptions = ComponentProps<typeof ContentfulImage>['options'];
